/**
 * Requirements on top of the missions module's backlog: keys, acceptance criteria, links,
 * quality checks, source sections for imports, and how an item's status follows its missions.
 *
 * Pure and browser-safe: the hub and the console run the same rules.
 */
import type { Id } from '../index.ts';
import { ITEM_KIND_LABEL, ITEM_WORKFLOW, LINK_LABEL, PARENT_KINDS, criterionText, fitStatus, statusLabel } from './missions.ts';
import type {
  AcceptanceCriterion,
  AcceptanceInput,
  BacklogItem,
  ItemKind,
  ItemLink,
  ItemStatus,
  LinkInput,
  LinkType,
  MissionReport,
  MissionState,
  PromptLink,
  SourceSection,
} from './missions.ts';

// ---------------------------------------------------------------------------
// Keys: QAWL-123
// ---------------------------------------------------------------------------

/**
 * A key prefix for a project: the letters of its first word, up to 4 ("Qawl-Agents" is QAWL),
 * or the initials when the first word is a single letter. A digit is added while it clashes.
 */
export function keyPrefixFor(name: string, taken: ReadonlySet<string> = new Set()): string {
  const words = name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  let base = (words[0] ?? '').replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase();
  if (base.length < 2) base = words.map((w) => w[0] ?? '').join('').replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase();
  if (base.length < 2) base = 'ITEM';
  let out = base;
  for (let n = 2; taken.has(out); n++) out = `${base}${n}`;
  return out;
}

export const KEY_PREFIX_RE = /^[A-Z][A-Z0-9]{1,9}$/;

export function formatKey(prefix: string, n: number): string {
  return `${prefix}-${n}`;
}

/** The number of a key ("QAWL-12" is 12), or null. */
export function keyNumber(key: string): number | null {
  const m = /-(\d+)$/.exec(key);
  return m ? Number(m[1]) : null;
}

// ---------------------------------------------------------------------------
// Acceptance criteria
// ---------------------------------------------------------------------------

/** Criteria with ids and defaults filled in. Ids of `prev` are kept where they still match. */
export function normalizeCriteria(input: readonly AcceptanceInput[], prev: readonly AcceptanceCriterion[] = []): AcceptanceCriterion[] {
  const used = new Set<string>();
  let n = 0;
  for (const p of prev) {
    const m = /^ac(\d+)$/.exec(p.id);
    if (m) n = Math.max(n, Number(m[1]));
  }
  for (const c of input) {
    const m = c.id ? /^ac(\d+)$/.exec(c.id) : null;
    if (m) n = Math.max(n, Number(m[1]));
  }
  return input.map((c) => {
    const old = c.id ? prev.find((p) => p.id === c.id) : undefined;
    let id = c.id && /^ac\d+$/.test(c.id) && !used.has(c.id) ? c.id : '';
    if (!id) id = `ac${++n}`;
    used.add(id);
    const gwt = c.format === 'gwt';
    const out: AcceptanceCriterion = {
      id,
      format: gwt ? 'gwt' : 'check',
      text: '',
      done: c.done ?? old?.done ?? false,
      checkedBy: old?.checkedBy ?? null,
      checkedAt: old?.checkedAt ?? null,
      evidence: old?.evidence ?? null,
    };
    if (gwt) {
      out.given = (c.given ?? '').trim();
      out.when = (c.when ?? '').trim();
      out.then = (c.then ?? '').trim();
    }
    out.text = gwt ? criterionText({ format: 'gwt', text: c.text ?? '', given: out.given, when: out.when, then: out.then }) : (c.text ?? '').trim();
    if (gwt && !out.given && !out.when && !out.then) out.text = (c.text ?? '').trim();
    // A change of wording unchecks it: what was verified is no longer what is asked.
    if (old && old.text !== out.text && c.done === undefined) {
      out.done = false;
      out.checkedBy = null;
      out.checkedAt = null;
      out.evidence = null;
    }
    return out;
  });
}

const GWT_RE = /^\s*(?:[-*+]\s*(?:\[[ xX]\]\s*)?|\d+[.)]\s*)?\**given\**\s+(.+?)[,;]?\s+\**when\**\s+(.+?)[,;]?\s+\**then\**\s+(.+?)\s*$/i;

/**
 * Criteria written as text, one per line: "Given a when b then c" lines become Given/When/Then,
 * "- [ ] x", "- x" and "1. x" lines become checklist lines. Multi-line Given/When/Then blocks
 * (each keyword on its own line) are joined.
 */
export function parseCriteria(text: string): AcceptanceInput[] {
  const out: AcceptanceInput[] = [];
  const lines = text.split(/\r?\n/);
  let block: { given?: string; when?: string; then?: string } | null = null;
  const flush = () => {
    if (block && (block.given || block.when || block.then)) out.push({ format: 'gwt', text: '', given: block.given ?? '', when: block.when ?? '', then: block.then ?? '' });
    block = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const one = GWT_RE.exec(line);
    if (one) {
      flush();
      out.push({ format: 'gwt', text: '', given: one[1]!.trim(), when: one[2]!.trim(), then: one[3]!.trim() });
      continue;
    }
    const kw = /^\s*(?:[-*+]\s*)?\**(given|when|then|and)\**\s+(.+)$/i.exec(line);
    if (kw) {
      const k = kw[1]!.toLowerCase();
      const v = kw[2]!.trim();
      if (k === 'given') {
        flush();
        block = { given: v };
      } else if (k === 'and') {
        if (block) {
          if (block.then) block.then += ` and ${v}`;
          else if (block.when) block.when += ` and ${v}`;
          else if (block.given) block.given += ` and ${v}`;
        } else out.push({ format: 'check', text: v });
      } else {
        block ??= {};
        if (k === 'when') block.when = v;
        else block.then = v;
      }
      continue;
    }
    flush();
    const item = /^\s*(?:[-*+]\s*(?:\[([ xX])\]\s*)?|\d+[.)]\s+)(.+)$/.exec(line);
    if (item) out.push({ format: 'check', text: item[2]!.trim(), ...(item[1] && item[1] !== ' ' ? { done: true } : {}) });
    else if (!/^#/.test(line)) out.push({ format: 'check', text: line });
  }
  flush();
  return out;
}

/**
 * What a mission's final message says about each criterion. Lines like "AC-2: PASS - tests
 * cover it" or "AC2 FAIL: no retry". AC numbers count the criteria in order, from 1.
 */
export function parseAcceptanceReport(text: string, criteria: readonly Pick<AcceptanceCriterion, 'id'>[]): MissionReport['acceptance'] {
  const out = new Map<string, { id: string; met: boolean; evidence: string }>();
  // Works on one line per criterion and on text whose line breaks were collapsed: the
  // evidence runs to the next "AC-n" or the end of the line.
  const re = /(?:^|[\s*>|-])\**AC[-\s]?(\d+)\s*\**\s*[:.)-]?\s*\**\s*(PASS(?:ED)?|MET|DONE|YES|OK|FAIL(?:ED)?|NOT\s+MET|NO|PARTIAL(?:LY)?(?:\s+MET)?)\b\**\s*[-:.,)]*\s*([^\n]*?)(?=\s*(?:[-*]\s*)?\**AC[-\s]?\d+\s*\**\s*[:.)-]?\s*\**\s*(?:PASS|MET|DONE|YES|OK|FAIL|NOT\s+MET|NO|PARTIAL)\b|\n|$)/gi;
  for (const m of text.matchAll(re)) {
    const n = Number(m[1]);
    const c = criteria[n - 1];
    if (!c) continue;
    const verdict = m[2]!.toUpperCase().replace(/\s+/g, ' ');
    const met = /^(PASS|PASSED|MET|DONE|YES|OK)$/.test(verdict);
    out.set(c.id, { id: c.id, met, evidence: m[3]!.trim().slice(0, 500) });
  }
  return [...out.values()];
}

// ---------------------------------------------------------------------------
// Status follows the mission
// ---------------------------------------------------------------------------

/**
 * Where an item goes when its mission reaches `state`, or null to leave it:
 *   running, waiting   in progress
 *   done               in review (done for kinds without a review step)
 *   failed, stopped    back to ready (or the first open status), with the reason
 * Items that are done or dropped never move.
 */
export function itemStatusForMission(kind: ItemKind, current: ItemStatus, state: MissionState): ItemStatus | null {
  if (current === 'done' || current === 'dropped') return null;
  const flow = ITEM_WORKFLOW[kind];
  let want: ItemStatus | null = null;
  if (state === 'running' || state === 'waiting') want = 'in_progress';
  else if (state === 'done') want = flow.includes('review') ? 'review' : 'done';
  else if (state === 'failed' || state === 'stopped') want = flow.includes('ready') ? 'ready' : flow[0]!;
  if (!want) return null;
  want = fitStatus(kind, want);
  return want === current ? null : want;
}

/**
 * The sentence an item shows under its status when a mission moved it. `blocked`: the label of
 * the approval block (APPROVAL_BLOCK_LABEL) that holds a waiting mission (R-HL-3): the item
 * stays in progress and says so, it is never sent to review.
 */
export function missionStatusNote(state: MissionState, missionTitle: string, reason?: string, blocked?: string): string {
  if (state === 'waiting' && blocked) return `Mission "${missionTitle}" is ${blocked.replace(/^Blocked/, 'blocked')}. Open the mission to see what it asked.`;
  switch (state) {
    case 'running':
    case 'waiting':
      return `Mission "${missionTitle}" is working on it.`;
    case 'done':
      return `Mission "${missionTitle}" finished. Review what it changed.`;
    case 'failed':
      return `Mission "${missionTitle}" failed${reason ? `: ${reason}` : '.'}`;
    case 'stopped':
      return `Mission "${missionTitle}" was stopped${reason ? `: ${reason}` : '.'}`;
    default:
      return `Mission "${missionTitle}" is planned.`;
  }
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

/** A link input as stored: "A is blocked by B" is stored as "B blocks A". */
export function canonicalLink(input: LinkInput): { type: LinkType; from: Id; to: Id } {
  return input.inverse ? { type: input.type, from: input.to, to: input.from } : { type: input.type, from: input.from, to: input.to };
}

export interface LinkRow {
  link: ItemLink;
  /** The item at the other end. */
  otherId: Id;
  /** "Blocks", "Blocked by", ... as read from this item. */
  label: string;
  outgoing: boolean;
}

/** An item's links as it reads them, grouped by label order. */
export function linksOf(itemId: Id, links: readonly ItemLink[]): LinkRow[] {
  const rows: LinkRow[] = [];
  for (const l of links) {
    if (l.from === itemId) rows.push({ link: l, otherId: l.to, label: LINK_LABEL[l.type].out, outgoing: true });
    else if (l.to === itemId) rows.push({ link: l, otherId: l.from, label: LINK_LABEL[l.type].in, outgoing: false });
  }
  return rows.sort((a, b) => a.label.localeCompare(b.label) || a.link.at - b.link.at);
}

/** What a mission prompt lists about an item's neighbours: its parent, then its links. */
export function promptLinksFor(it: Pick<BacklogItem, 'id' | 'parentId'>, items: ReadonlyMap<Id, BacklogItem>, links: readonly ItemLink[]): PromptLink[] {
  const out: PromptLink[] = [];
  for (const l of links) {
    const outgoing = l.from === it.id;
    if (!outgoing && l.to !== it.id) continue;
    const other = items.get(outgoing ? l.to : l.from);
    if (!other) continue;
    out.push({ label: outgoing ? LINK_LABEL[l.type].out : LINK_LABEL[l.type].in, key: other.key, title: other.title, status: statusLabel(other.kind, other.status) });
  }
  const parent = it.parentId ? items.get(it.parentId) : undefined;
  if (parent) out.unshift({ label: `Part of ${ITEM_KIND_LABEL[parent.kind].toLowerCase()}`, key: parent.key, title: parent.title, status: statusLabel(parent.kind, parent.status) });
  return out;
}

/** Open items that block this one. */
export function openBlockers(itemId: Id, links: readonly ItemLink[], items: ReadonlyMap<Id, Pick<BacklogItem, 'status'>>): Id[] {
  return links
    .filter((l) => l.type === 'blocks' && l.to === itemId)
    .map((l) => l.from)
    .filter((id) => {
      const s = items.get(id)?.status;
      return s && s !== 'done' && s !== 'dropped';
    });
}

/** A kind with its article: "an epic", "a story"; capitalised: "An epic". */
export function kindWord(kind: ItemKind, capital = false): string {
  const w = ITEM_KIND_LABEL[kind].toLowerCase();
  const out = /^[aeiou]/.test(w) ? `an ${w}` : `a ${w}`;
  return capital ? out[0]!.toUpperCase() + out.slice(1) : out;
}

/** The parent kinds a kind may sit under, as words: "an epic or a feature". */
export function parentKindsText(kind: ItemKind): string {
  const ks = PARENT_KINDS[kind].map((k) => ITEM_KIND_LABEL[k].toLowerCase());
  if (!ks.length) return 'nothing';
  const art = (w: string) => (/^[aeiou]/.test(w) ? `an ${w}` : `a ${w}`);
  return ks.length === 1 ? art(ks[0]!) : `${ks.slice(0, -1).map(art).join(', ')} or ${art(ks[ks.length - 1]!)}`;
}

/** Why `parent` cannot hold `kind`, or null when it can. */
export function parentKindError(kind: ItemKind, parentKind: ItemKind): string | null {
  if (PARENT_KINDS[kind].includes(parentKind)) return null;
  if (!PARENT_KINDS[kind].length) return `${ITEM_KIND_LABEL[kind]}s sit at the top. They cannot have a parent.`;
  return `${kindWord(kind, true)} sits under ${parentKindsText(kind)}, not under ${kindWord(parentKind)}.`;
}

// ---------------------------------------------------------------------------
// Quality checks (detect)
// ---------------------------------------------------------------------------

/** Words that make a requirement hard to test. Each says what to write instead. */
export const AMBIGUOUS_WORDS: Readonly<Record<string, string>> = {
  fast: 'a time limit, for example "within 2 seconds"',
  quick: 'a time limit',
  quickly: 'a time limit',
  slow: 'a time limit',
  easy: 'the steps or the number of clicks',
  easily: 'the steps or the number of clicks',
  simple: 'what exactly is allowed',
  intuitive: 'what a first-time user must manage without help',
  'user-friendly': 'a measurable outcome',
  'user friendly': 'a measurable outcome',
  robust: 'the failures it must survive',
  efficient: 'a limit on time, memory or cost',
  flexible: 'the cases it must handle',
  seamless: 'what must not happen',
  seamlessly: 'what must not happen',
  appropriate: 'which one',
  appropriately: 'how exactly',
  adequate: 'the minimum',
  reasonable: 'a number',
  several: 'a number',
  some: 'which ones or how many',
  many: 'a number',
  few: 'a number',
  etc: 'the full list',
  'and/or': 'and, or or',
  'as needed': 'when exactly',
  'if possible': 'whether it is required',
  'as appropriate': 'which cases',
  approximately: 'a range',
  minimal: 'a limit',
  maximize: 'a target',
  minimize: 'a limit',
  optimal: 'a target',
  'state of the art': 'the capability',
  tbd: 'the decision',
  normally: 'the cases',
  usually: 'the cases',
  better: 'than what, by how much',
  improved: 'by how much',
  'should be able to': 'can',
};

/** Ambiguous words and phrases in `text`, each once, in the order found. */
export function findAmbiguous(text: string): string[] {
  const lower = ` ${text.toLowerCase().replace(/[`*_]/g, ' ')} `;
  const found: { w: string; at: number }[] = [];
  for (const w of Object.keys(AMBIGUOUS_WORDS)) {
    const esc = w.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
    const m = new RegExp(`(^|[^a-z0-9-])${esc}(?=$|[^a-z0-9-])`, 'i').exec(lower);
    if (m) found.push({ w, at: m.index });
  }
  // A phrase hides the single words inside it ("as needed" over "needed").
  const words = found.sort((a, b) => a.at - b.at).map((f) => f.w);
  return words.filter((w) => !words.some((o) => o !== w && o.includes(w) && o.length > w.length));
}

export type QualityCode =
  | 'no_criteria'
  | 'untestable_criterion'
  | 'ambiguous'
  | 'too_large'
  | 'no_parent'
  | 'no_affects'
  | 'no_repro'
  | 'duplicate'
  | 'no_description'
  | 'blocked';

export type QualityFix =
  | { kind: 'ai'; task: 'criteria' | 'split' | 'improve' | 'estimate' }
  | { kind: 'pick-parent' }
  | { kind: 'pick-affects' }
  | { kind: 'mark-duplicate'; otherId: Id }
  | { kind: 'edit' };

export interface QualityIssue {
  code: QualityCode;
  /** warn: fix before work starts; info: worth a look. */
  level: 'warn' | 'info';
  message: string;
  fix?: QualityFix;
  /** The fix as a button label: verb plus object. */
  fixLabel?: string;
  criterionId?: string;
  words?: string[];
  otherId?: Id;
}

export const QUALITY_LABEL: Record<QualityCode, string> = {
  no_criteria: 'No acceptance criteria',
  untestable_criterion: 'Untestable criterion',
  ambiguous: 'Ambiguous wording',
  too_large: 'Too large',
  no_parent: 'No parent',
  no_affects: 'Affects nothing',
  no_repro: 'No steps to reproduce',
  duplicate: 'Possible duplicate',
  no_description: 'No description',
  blocked: 'Blocked',
};

/** Points above which a story should be split. */
export const LARGE_ESTIMATE = 8;
/** Criteria above which a story should be split. */
export const LARGE_CRITERIA = 8;

const STOP = new Set(['a', 'an', 'the', 'to', 'of', 'in', 'on', 'for', 'and', 'or', 'is', 'be', 'as', 'with', 'by', 'at', 'it', 'when', 'can', 'should', 'user', 'users', 'i', 'we', 'so', 'that', 'from', 'not', 'are']);

/**
 * Each title's words, built once: likelyDuplicates compares every pair of a backlog, and on 258
 * items building them per pair took 2 s of every 5 on the Missions page (22 Oct 2026).
 */
const tokenCache = new Map<string, Set<string>>();
const TOKEN_CACHE_MAX = 5000;

function tokens(s: string): Set<string> {
  const hit = tokenCache.get(s);
  if (hit) return hit;
  const out = new Set(
    s
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 1 && !STOP.has(w))
      .map((w) => w.replace(/(ing|ed|es|s)$/, '')),
  );
  if (tokenCache.size >= TOKEN_CACHE_MAX) tokenCache.clear();
  tokenCache.set(s, out);
  return out;
}

/** Word overlap of two titles, 0..1 (Jaccard over stemmed words without stop words). */
export function titleSimilarity(a: string, b: string): number {
  const x = tokens(a);
  const y = tokens(b);
  if (!x.size || !y.size) return 0;
  let both = 0;
  for (const w of x) if (y.has(w)) both++;
  return both / (x.size + y.size - both);
}

export const DUPLICATE_AT = 0.6;

/** Open items in the same project whose titles read nearly the same, most similar first. */
export function likelyDuplicates(item: Pick<BacklogItem, 'id' | 'projectId' | 'title' | 'kind'>, items: readonly Pick<BacklogItem, 'id' | 'projectId' | 'title' | 'kind' | 'status'>[], at = DUPLICATE_AT): { id: Id; score: number }[] {
  const family = (k: ItemKind) => (k === 'epic' || k === 'feature' ? 'big' : 'small');
  return items
    .filter((o) => o.id !== item.id && o.projectId === item.projectId && o.status !== 'dropped' && family(o.kind) === family(item.kind))
    .map((o) => ({ id: o.id, score: titleSimilarity(item.title, o.title) }))
    .filter((x) => x.score >= at)
    .sort((a, b) => b.score - a.score);
}

/** Criteria that cannot be checked: too short, no outcome, or ambiguous. */
export function criterionProblem(c: Pick<AcceptanceCriterion, 'format' | 'text' | 'given' | 'when' | 'then'>): string | null {
  const text = criterionText(c);
  if (text.split(/\s+/).filter(Boolean).length < 3) return 'It is too short to check. Say what is observed and when.';
  if (c.format === 'gwt' && !(c.then ?? '').trim() && !/\bthen\b/i.test(c.text)) return 'It has no Then: say what must be observed.';
  const amb = findAmbiguous(text);
  if (amb.length) return `"${amb[0]}" cannot be checked. Write ${AMBIGUOUS_WORDS[amb[0]!]} instead.`;
  return null;
}

/** A story too large for one mission, as the size that says so ('13 points'); null otherwise. */
export function tooLarge(item: Pick<BacklogItem, 'kind' | 'estimate' | 'acceptance'>): string | null {
  if (item.kind !== 'story') return null;
  if ((item.estimate ?? 0) > LARGE_ESTIMATE) return `${item.estimate} points`;
  if (item.acceptance.length > LARGE_CRITERIA) return `${item.acceptance.length} acceptance criteria`;
  return null;
}

export interface QualityContext {
  items: readonly BacklogItem[];
  links: readonly ItemLink[];
}

/**
 * What to fix before an item is worked, cheapest first. Done and dropped items, and epics and
 * features apart from wording, get fewer checks.
 */
export function qualityIssues(item: BacklogItem, ctx: QualityContext): QualityIssue[] {
  const out: QualityIssue[] = [];
  if (item.status === 'done' || item.status === 'dropped') return out;
  const workable = item.kind === 'story' || item.kind === 'task' || item.kind === 'defect' || item.kind === 'spike' || item.kind === 'chore';
  const kindWord = ITEM_KIND_LABEL[item.kind].toLowerCase();

  const hasRepro = !!item.defect && !!(item.defect.steps.trim() || item.defect.actual.trim());
  if (!item.body.trim() && item.kind !== 'task' && item.kind !== 'chore' && !hasRepro) {
    out.push({ code: 'no_description', level: 'info', message: `The ${kindWord} has no description, so a mission only has its title to go on.`, fix: { kind: 'ai', task: 'improve' }, fixLabel: 'Write description' });
  }

  if ((item.kind === 'story' || item.kind === 'defect') && !item.acceptance.length) {
    out.push({ code: 'no_criteria', level: 'warn', message: 'Nothing says when it is done. Add acceptance criteria a mission can check.', fix: { kind: 'ai', task: 'criteria' }, fixLabel: 'Generate criteria' });
  }
  const untestable = item.acceptance.map((c) => ({ c, p: criterionProblem(c) })).filter((x) => x.p);
  if (untestable.length > 2) {
    // Many at once read as one finding, not a wall of them.
    const names = untestable.slice(0, 4).map((x) => `"${criterionText(x.c)}"`).join(', ');
    out.push({ code: 'untestable_criterion', level: 'warn', message: `${untestable.length} of ${item.acceptance.length} criteria cannot be checked as written: ${names}${untestable.length > 4 ? ', and more' : ''}. Say what is observed, and when.`, fix: { kind: 'ai', task: 'improve' }, fixLabel: 'Rewrite criteria' });
  } else {
    for (const { c, p } of untestable) out.push({ code: 'untestable_criterion', level: 'warn', message: `${criterionText(c) || 'A criterion'}: ${p}`, criterionId: c.id, fix: { kind: 'ai', task: 'improve' }, fixLabel: 'Rewrite criteria' });
  }

  const amb = findAmbiguous(`${item.title}\n${item.body}`);
  if (amb.length) {
    out.push({
      code: 'ambiguous',
      level: 'info',
      message: `${amb.map((w) => `"${w}"`).join(', ')} ${amb.length === 1 ? 'is' : 'are'} open to reading. Write ${AMBIGUOUS_WORDS[amb[0]!]}${amb.length > 1 ? ', and so on' : ''}.`,
      words: amb,
      fix: { kind: 'ai', task: 'improve' },
      fixLabel: 'Improve wording',
    });
  }

  const large = tooLarge(item);
  if (large) {
    out.push({ code: 'too_large', level: 'warn', message: `At ${large} it is too large for one mission. Split it into stories that each deliver something.`, fix: { kind: 'ai', task: 'split' }, fixLabel: 'Split story' });
  }

  if (!item.parentId && PARENT_KINDS[item.kind].length && item.kind !== 'defect' && item.kind !== 'task') {
    out.push({ code: 'no_parent', level: 'info', message: `It is not under ${parentKindsText(item.kind)}, so it does not roll up anywhere.`, fix: { kind: 'pick-parent' }, fixLabel: 'Choose parent' });
  }

  if (item.kind === 'defect') {
    if (!ctx.links.some((l) => l.type === 'affects' && l.from === item.id) && !item.parentId) {
      out.push({ code: 'no_affects', level: 'info', message: 'It does not say what it affects. Link the story or feature it breaks.', fix: { kind: 'pick-affects' }, fixLabel: 'Link affected item' });
    }
    const d = item.defect;
    if (!d || !d.steps.trim() || !d.expected.trim() || !d.actual.trim()) {
      out.push({ code: 'no_repro', level: 'warn', message: 'Steps to reproduce, expected and actual results are needed to confirm a fix.', fix: { kind: 'ai', task: 'improve' }, fixLabel: 'Draft repro steps' });
    }
  }

  if (workable) {
    const byId = new Map(ctx.items.map((i) => [i.id, i]));
    const blockers = openBlockers(item.id, ctx.links, byId);
    if (blockers.length) {
      const first = byId.get(blockers[0]!);
      out.push({ code: 'blocked', level: 'info', message: `Blocked by ${first ? `${first.key} ${first.title}` : 'another item'}${blockers.length > 1 ? ` and ${blockers.length - 1} more` : ''}, which ${blockers.length > 1 ? 'are' : 'is'} not done.`, otherId: blockers[0] });
    }
  }

  const linkedDup = new Set(ctx.links.filter((l) => l.type === 'duplicates' && (l.from === item.id || l.to === item.id)).map((l) => (l.from === item.id ? l.to : l.from)));
  const dup = likelyDuplicates(item, ctx.items).find((d) => !linkedDup.has(d.id));
  if (dup) {
    const other = ctx.items.find((i) => i.id === dup.id)!;
    out.push({ code: 'duplicate', level: 'info', message: `It reads like ${other.key} "${other.title}".`, otherId: other.id, fix: { kind: 'mark-duplicate', otherId: other.id }, fixLabel: 'Mark as duplicate' });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Source documents: sections for traceability
// ---------------------------------------------------------------------------

export const SECTION_TEXT_MAX = 6_000;

/**
 * Split a document into numbered sections by its headings (Markdown #, setext underlines, or
 * numbered lines such as "3.2 Refunds"). Ids follow the outline: S1, S1.1, S1.2, S2. Text
 * before the first heading is S0. A document with no headings is split by paragraphs into
 * sections of about 1,500 characters.
 */
export function splitSections(text: string): SourceSection[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  type Head = { line: number; level: number; title: string };
  const heads: Head[] = [];
  let fence = false;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]!;
    if (/^\s*(```|~~~)/.test(l)) fence = !fence;
    if (fence) continue;
    const md = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(l);
    if (md) {
      heads.push({ line: i, level: md[1]!.length, title: md[2]!.trim() });
      continue;
    }
    const next = lines[i + 1];
    if (l.trim() && next !== undefined && /^\s*(=+|-+)\s*$/.test(next) && next.trim().length >= 3 && !/^\s*[-*]/.test(l)) {
      heads.push({ line: i, level: next.trim()[0] === '=' ? 1 : 2, title: l.trim() });
      i++;
      continue;
    }
    const num = /^\s*(\d+(?:\.\d+){0,3})\.?\s+([A-Z][^.!?]{1,80})$/.exec(l);
    if (num && (i === 0 || !lines[i - 1]!.trim()) && l.length < 90) {
      heads.push({ line: i, level: num[1]!.split('.').length, title: `${num[1]} ${num[2]!.trim()}` });
    }
  }
  const out: SourceSection[] = [];
  const clipText = (s: string) => {
    const t = s.replace(/\n{3,}/g, '\n\n').trim();
    return t.length > SECTION_TEXT_MAX ? `${t.slice(0, SECTION_TEXT_MAX)}\n[...]` : t;
  };
  if (!heads.length) {
    const paras = text.replace(/\r\n?/g, '\n').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    let buf = '';
    let n = 0;
    const push = () => {
      if (!buf.trim()) return;
      n++;
      const first = buf.trim().split('\n')[0]!.slice(0, 60);
      out.push({ id: `S${n}`, title: first, level: 1, text: clipText(buf) });
      buf = '';
    };
    for (const p of paras) {
      if (buf && buf.length + p.length > 1500) push();
      buf += (buf ? '\n\n' : '') + p;
    }
    push();
    return out;
  }
  const pre = lines.slice(0, heads[0]!.line).join('\n').trim();
  if (pre) out.push({ id: 'S0', title: 'Introduction', level: 1, text: clipText(pre) });
  const minLevel = Math.min(...heads.map((h) => h.level));
  const counters: number[] = [];
  heads.forEach((h, k) => {
    const depth = Math.max(0, Math.min(5, h.level - minLevel));
    counters.length = depth + 1;
    for (let d = 0; d < depth; d++) counters[d] ??= 1;
    counters[depth] = (counters[depth] ?? 0) + 1;
    const id = `S${counters.slice(0, depth + 1).map((c) => c ?? 1).join('.')}`;
    const end = k + 1 < heads.length ? heads[k + 1]!.line : lines.length;
    const body = lines.slice(h.line + 1, end).join('\n').replace(/^\s*(=+|-+)\s*$/m, '');
    out.push({ id, title: h.title, level: depth + 1, text: clipText(body) });
  });
  return out;
}

/** The heading path of a section: "Payments > Refunds". */
export function sectionPath(sections: readonly SourceSection[], id: string): string {
  const s = sections.find((x) => x.id === id);
  if (!s) return id;
  const parts: string[] = [];
  const segs = id.replace(/^S/, '').split('.');
  for (let i = 1; i <= segs.length; i++) {
    const p = sections.find((x) => x.id === `S${segs.slice(0, i).join('.')}`);
    if (p) parts.push(p.title);
  }
  return parts.join(' > ') || s.title;
}

// ---------------------------------------------------------------------------
// Trees and ordering
// ---------------------------------------------------------------------------

/** Siblings in rank order, then oldest first. */
export function byRank(a: Pick<BacklogItem, 'rank' | 'createdAt' | 'id'>, b: Pick<BacklogItem, 'rank' | 'createdAt' | 'id'>): number {
  return a.rank - b.rank || a.createdAt - b.createdAt || a.id.localeCompare(b.id);
}

/** A rank between two neighbours (either may be missing). */
export function rankBetween(before: number | null, after: number | null): number {
  if (before == null && after == null) return 1000;
  if (before == null) return after! - 1000;
  if (after == null) return before + 1000;
  return (before + after) / 2;
}

/** The children of an epic or feature a sequence would run, in order: open, workable, by rank, depth first. */
export function sequenceCandidates(rootId: Id, items: readonly BacklogItem[]): BacklogItem[] {
  const out: BacklogItem[] = [];
  const walk = (pid: Id, depth: number) => {
    if (depth > 10) return;
    for (const c of items.filter((i) => i.parentId === pid).sort(byRank)) {
      if (c.status === 'done' || c.status === 'dropped') continue;
      if (c.kind === 'story' || c.kind === 'task' || c.kind === 'defect' || c.kind === 'spike' || c.kind === 'chore') {
        out.push(c);
      } else walk(c.id, depth + 1);
    }
  };
  walk(rootId, 0);
  return out;
}

